import React, { FC, useEffect, useMemo, useState } from 'react';

import { CardSchema, EDiscoverItemLayoutOptions, HighlightSchema } from 'constants/graphqlTypes';
import { TEST_CARD_COMMON, TEST_ID_CARD_VIEW_START } from 'constants/aqa/card';
import { EFeaturedFlags } from 'types/EFeatureFlags';

import useOpenHiddenCard from 'helpers/useOpenHiddenCards';
import ReactionMenu from 'components/Reactions/ReactionMenu';
import ReactionsModal from 'components/Reactions/ReactionsModal/ReactionsModal';
import HiddenWordsAlert from 'components/HiddenWordsAlert';
import usePageType from 'helpers/usePageType';
import { useFeaturedFlagEnabled } from 'helpers/posthogHooks';
import { useCurrentList } from 'graphQL/list/hooks';
import getAuthUser from 'helpers/getAuthUser';
import { useReactionMenu } from 'components/Reactions/helpers/hooks';
import { StyledWrap, StyledTitle } from 'components/Collection/styled';
import { ReactionsProvider, useReactionContextState } from 'components/Reactions/hooks';
import useReferrerId from 'helpers/useReferrerId';
import { useIsUserAllowedToReact } from './helpers/hooks';
import Common from './Common';
import FirstUp from './FirstUp';
import FullBleed from './FullBleed';
import Immersive from './Immersive';
import List from './List';

interface ICardProps {
  card: CardSchema;
  layout?: EDiscoverItemLayoutOptions | null;
  isDark?: boolean;
  showActivity?: boolean | null;
  highlight?: HighlightSchema | null;
  disableLinks?: boolean;
  dataTestId?: string;
  shouldDisableLongTap?: boolean;
  isProfilePicks?: boolean | null;
  fontColor?: string;
  hideFooterAvatars?: boolean;
  disableAnimation?: boolean;
  hideActionsAvatar?: boolean;
  hideActionsUserInfo?: boolean;
  hideTopAvatars?: boolean;
  isSpotlight?: boolean;
  isSmall?: boolean;
  isCarousel?: boolean;
  showAddReaction?: boolean;
}

const Card: FC<ICardProps> = ({
  card,
  layout,
  isDark = true,
  showActivity,
  highlight,
  disableLinks,
  dataTestId,
  shouldDisableLongTap,
  isProfilePicks,
  fontColor,
  hideFooterAvatars,
  disableAnimation,
  hideActionsAvatar,
  hideActionsUserInfo,
  hideTopAvatars,
  isSpotlight,
  isSmall,
  isCarousel,
  showAddReaction,
}) => {
  const [isDisableArea, setDisableArea] = useState(false);
  const [isReactionButtonMenuShown, setIsReactionButtonMenuShown] = useState(false);

  const {
    reactionCoordinates,
    isQuickReactionOpen,
    setIsQuickReactionOpen,
    isReactionsModalOpen,
    setIsReactionsModalOpen,
    infoRef,
  } = useReactionMenu();

  const {
    isDiscovery,
    isFollowingTab,
    isEveryoneTab,
    isSearch,
    isAddThought,
    isPickAddStyle,
    isAllPicks,
    isAddSparkResponseStyle,
    isListDetail,
    isSparkDetail,
    isCardDetail,
  } = usePageType();
  const {
    setThoughtId,
    thoughtId,
    setPickerId,
    pickerId,
    cardId,
    setCardId,
    user,
    setUser,
  } = useReactionContextState();
  const { openCard, openIds } = useOpenHiddenCard();
  const { userId } = getAuthUser();
  const isInteractSparks = useFeaturedFlagEnabled(EFeaturedFlags.InteractSpark);

  const { isCollaborative } = useCurrentList() ?? {};
  const isCurrentUserPick = useMemo(() => pickerId === userId, [pickerId]);

  const isCardAllowedToReact = useIsUserAllowedToReact(card?.extraData?.items?.[0]?.user, shouldDisableLongTap);
  const isFallbackAllowedToReact = useIsUserAllowedToReact(user, shouldDisableLongTap);
  const isCardBlocked = (!thoughtId || isAllPicks) && !isCardAllowedToReact;
  const isFallbackBlocked = !!thoughtId && !isFallbackAllowedToReact;

  const isDisableReaction =
    (isDiscovery && !layout) ||
    (isCollaborative && !layout) ||
    isCurrentUserPick ||
    (isSearch && !layout) ||
    isAddThought ||
    (isSparkDetail && !isInteractSparks);

  const isDisableHiddenAlert = isAddThought || isPickAddStyle || isAddSparkResponseStyle;
  const isCardReaction = isCardDetail && (card?.extraData?.items?.length || 0) < 2;
  const isCardOpen = isCardDetail && isCardReaction;
  const firstActionInCard = card?.extraData?.items?.[0];

  const isShowTitle =
    card?.isHidden && isCardOpen && (!!layout || (!layout && isAllPicks) || (!layout && isListDetail));

  const cardDataTestId = layout ? `${TEST_ID_CARD_VIEW_START}_${layout?.toLowerCase()}` : TEST_CARD_COMMON;

  const referrerId = useReferrerId();
  const actionByUserWithCurrentRefId = card.extraData?.items?.find((action) => action?.user?.id === referrerId);

  useEffect(() => {
    if (!isCardReaction) {
      setThoughtId(card?.extraData?.items?.[0]?.id ?? null);
      setUser(card?.extraData?.items?.[0]?.user ?? null);
      setPickerId(card?.pickedByUsers?.items?.[0]?.id ?? referrerId ?? null);
    }

    if (!isCardDetail && referrerId && actionByUserWithCurrentRefId?.__typename === 'CollectionCardLinkSchema') {
      setPickerId(referrerId);
      setCardId(actionByUserWithCurrentRefId.cardPickId ?? null);
    }

    if (firstActionInCard?.__typename !== 'CollectionCardLinkSchema') {
      setCardId(card.id);
    }

    if (isCardDetail && (card?.extraData?.items?.length || 0) < 2) {
      setPickerId(card?.pickedByUsers?.items?.[0]?.id ?? referrerId ?? null);
    }
  }, []);

  useEffect(() => {
    if (isDisableArea) {
      setIsQuickReactionOpen(false);
    }
  }, [isDisableArea, isQuickReactionOpen]);

  return !card.isHidden || isCardOpen || isDisableHiddenAlert ? (
    <ReactionsProvider
      value={{
        isQuickReactionOpen,
        setThoughtId,
        thoughtId,
        setPickerId,
        pickerId,
        cardId,
        setCardId,
        user,
        setUser,
      }}
    >
      <StyledWrap
        data-testid={dataTestId ?? cardDataTestId}
        ref={infoRef}
        disableLinks={isQuickReactionOpen || disableLinks}
        isDark={isDark}
        isNewInFeed={!!card?.isNewInChannels}
      >
        <div>
          {isShowTitle && (
            <StyledTitle isDark={isDark} removePaddingTop={!layout}>
              Hidden {isDiscovery || isFollowingTab || isEveryoneTab ? 'content' : 'pick'}
            </StyledTitle>
          )}
          {!layout && (
            <Common
              dataTestId={dataTestId}
              card={card}
              isDark={isDark}
              showActivity={showActivity}
              disableReactionCallback={(value) => setDisableArea(value)}
              isProfilePicks={!!isProfilePicks}
              fontColor={fontColor}
              hideFooterAvatars={hideFooterAvatars}
              disableAnimation={disableAnimation}
              hideActionsAvatar={hideActionsAvatar}
              hideActionsUserInfo={hideActionsUserInfo}
              hideTopAvatars={hideTopAvatars}
              isSpotlight={isSpotlight}
              isSmall={isSmall}
              isCarousel={isCarousel}
              showAddReaction={showAddReaction}
            />
          )}
          {layout === EDiscoverItemLayoutOptions.FirstUp && (
            <FirstUp
              dataTestId={dataTestId}
              card={card}
              isDark={isDark}
              highlight={highlight}
              isQuickReactionOpen={isQuickReactionOpen}
              isReactionButtonMenuShown={isReactionButtonMenuShown}
              setIsReactionButtonMenuShown={setIsReactionButtonMenuShown}
            />
          )}
          {layout === EDiscoverItemLayoutOptions.FullBleed && (
            <FullBleed dataTestId={dataTestId} card={card} highlight={highlight} />
          )}
          {layout === EDiscoverItemLayoutOptions.Immersive && (
            <Immersive dataTestId={dataTestId} card={card} highlight={highlight} />
          )}
          {layout === EDiscoverItemLayoutOptions.List && <List dataTestId={dataTestId} card={card} isDark={isDark} />}
        </div>
        {reactionCoordinates && reactionCoordinates.x && reactionCoordinates.y && !isDisableReaction && (
          <ReactionMenu
            isOpen={isQuickReactionOpen}
            anchorPoint={reactionCoordinates}
            onClose={() => setIsQuickReactionOpen(false)}
            onSend={() => {}}
            onEmoji={() => {}}
            onOpenReactionsModal={() => setIsReactionsModalOpen(true)}
          />
        )}
        {isReactionsModalOpen && <ReactionsModal id={card?.id} handleClose={() => setIsReactionsModalOpen(false)} />}
      </StyledWrap>
    </ReactionsProvider>
  ) : (
    <HiddenWordsAlert
      itemTestId={dataTestId ?? cardDataTestId}
      showHiddenContent={() => openCard?.(card.cardId)}
      isDark={isDark}
      isSmall={!layout}
      hasBottomMargin
    />
  );
};

export default Card;
